import { useLanguageAwareRouting, usePagination } from '@gofranz/common-components';
import type { IssueResponse } from '@gofranz/formshive-common';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Group,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../components/Common/EmptyState';
import { PageHeader } from '../../../components/Common/PageHeader';
import { useRustyState } from '../../../state';

export function AccountIssuesStartPage() {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const { createLanguageURL } = useLanguageAwareRouting();
  const [dismissedFilter, setDismissedFilter] = useState<string>('active');
  const dismissedFilterRef = useRef(dismissedFilter);

  const fetchIssues = useCallback(
    async (params: { nextPage: number; [key: string]: any }) => {
      const { nextPage } = params;
      const filter = dismissedFilterRef.current;
      const dismissed = filter === 'active' ? false : filter === 'dismissed' ? true : undefined;
      const response = await api.getIssues({
        offset: nextPage === 1 ? 0 : 10 * (nextPage - 1),
        limit: 10,
        dismissed,
      });
      return {
        data: response.data || [],
        total: response.total || 0,
      };
    },
    [api]
  );

  const pagination = usePagination<IssueResponse>({
    perPage: 10,
    fetchData: fetchIssues,
  });

  const handleFilterChange = (value: string) => {
    setDismissedFilter(value);
    dismissedFilterRef.current = value;
    pagination.loadPage(1);
  };

  const handleDismiss = async (id: string) => {
    try {
      await api.dismissIssue(id);
      pagination.loadPage(pagination.page);
    } catch (err) {
      console.error('Failed to dismiss issue:', err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  const getIssueTypeLabel = (issueType: string) => {
    const key = `issues.types.${issueType}`;
    const translated = t(key);
    return translated !== key ? translated : issueType;
  };

  const getIssueCodeLabel = (issueCode: string) => {
    const key = `issues.codes.${issueCode}`;
    const translated = t(key);
    return translated !== key ? translated : issueCode;
  };

  const columns = [
    {
      accessor: 'created_at',
      title: t('issues.createdAt'),
      render: (row: IssueResponse) => <Text size="sm">{formatDate(row.created_at)}</Text>,
    },
    {
      accessor: 'form_title',
      title: t('issues.formName'),
      render: (row: IssueResponse) =>
        row.form_id ? (
          <Anchor href={`/#${createLanguageURL(`/account/forms/${row.form_id}`)}`} size="sm">
            {row.form_title || row.form_id.slice(0, 8)}
          </Anchor>
        ) : (
          <Text size="sm" c="dimmed">
            -
          </Text>
        ),
    },
    {
      accessor: 'issue_type',
      title: t('issues.issueType'),
      render: (row: IssueResponse) => (
        <Badge variant="light">{getIssueTypeLabel(row.issue_type)}</Badge>
      ),
    },
    {
      accessor: 'issue_code',
      title: t('issues.issueCode'),
      render: (row: IssueResponse) => (
        <Badge variant="light" color="gray">
          {getIssueCodeLabel(row.issue_code)}
        </Badge>
      ),
    },
    {
      accessor: 'details',
      title: t('issues.details'),
      render: (row: IssueResponse) => (
        <Text size="sm" lineClamp={2}>
          {row.details || '-'}
        </Text>
      ),
    },
    {
      accessor: 'message_id',
      title: t('issues.message'),
      render: (row: IssueResponse) =>
        row.message_id ? (
          <Anchor href={createLanguageURL(`/account/message/${row.message_id}`)} size="sm">
            {t('issues.viewMessage')}
          </Anchor>
        ) : (
          <Text size="sm" c="dimmed">
            -
          </Text>
        ),
    },
    {
      accessor: 'actions',
      title: '',
      render: (row: IssueResponse) =>
        !row.dismissed ? (
          <Tooltip label={t('issues.dismiss')}>
            <ActionIcon variant="subtle" color="green" onClick={() => handleDismiss(row.id)}>
              <IconCheck size={16} />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Badge color="gray" variant="light">
            {t('issues.dismissed')}
          </Badge>
        ),
    },
  ];

  return (
    <>
      <PageHeader title={t('issues.title')} subtitle={t('issues.description')} />
      <Stack gap="md">
        <Group>
          <SegmentedControl
            value={dismissedFilter}
            onChange={handleFilterChange}
            data={[
              { label: t('issues.active'), value: 'active' },
              { label: t('issues.dismissed'), value: 'dismissed' },
              { label: t('issues.all'), value: 'all' },
            ]}
          />
        </Group>
        {pagination.error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {pagination.error}
          </Alert>
        )}
        <DataTable
          minHeight={(pagination.data?.length ?? 0) === 0 ? 280 : undefined}
          emptyState={
            <EmptyState
              icon={IconAlertTriangle}
              title={t('issues.noIssuesFound')}
              description={t(
                'issues.emptyStateDescription',
                'Delivery problems with auto-responses, notifications, and integrations show up here.'
              )}
            />
          }
          withTableBorder
          borderRadius="md"
          shadow="xs"
          records={pagination.data || []}
          columns={columns}
          totalRecords={pagination.total}
          recordsPerPage={pagination.perPage}
          page={pagination.page}
          onPageChange={pagination.setPage}
          fetching={pagination.loading}
          noRecordsText={t('issues.noIssuesFound')}
        />
      </Stack>
    </>
  );
}
